// styles.js
// Modern, professional styles for Login components

import { COLORS, LAYOUT, TYPOGRAPHY, CARD_STYLES, BUTTON_STYLES } from '../../constants';

export const styles = {
  // Main container - full screen with modern layout
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: LAYOUT.containerPadding,
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    position: 'relative',
  },

  // Background decoration
  backgroundDecoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, ${COLORS.primary}08 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${COLORS.secondary}06 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },

  // Login card - modern card design
  loginCard: {
    ...CARD_STYLES.elevated,
    width: '100%',
    maxWidth: '480px',
    padding: '48px',
    position: 'relative',
    zIndex: 2,
    background: COLORS.white,
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${COLORS.primary}10`,
  },

  // Header section
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },

  // ISU logo/brand section
  brandSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },

  brandIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px',
    boxShadow: `0 4px 16px ${COLORS.shadow}`,
  },

  brandText: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.primary,
    fontWeight: '700',
    fontSize: '28px',
    margin: 0,
  },

  title: {
    ...TYPOGRAPHY.heading1,
    color: COLORS.text.primary,
    fontWeight: '700',
    fontSize: '32px',
    marginBottom: '12px',
    letterSpacing: '-0.02em',
  },

  subtitle: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    lineHeight: 1.6,
    margin: 0,
  },

  // Form section
  formSection: {
    marginBottom: '32px',
  },

  inputGroup: {
    marginBottom: '24px',
    textAlign: 'left',
  },

  inputLabel: {
    display: 'block',
    ...TYPOGRAPHY.body,
    color: COLORS.text.primary,
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
  },

  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  inputIcon: {
    position: 'absolute',
    left: '16px',
    zIndex: 2,
    color: COLORS.text.secondary,
  },

  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    fontSize: '16px',
    border: `2px solid ${COLORS.grayLight}`,
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    fontFamily: 'inherit',
    '::placeholder': {
      color: COLORS.text.secondary,
    },
  },

  inputFocused: {
    borderColor: COLORS.primary,
    boxShadow: `0 0 0 4px ${COLORS.primary}15`,
    transform: 'translateY(-2px)',
  },

  // Button styles
  loginButton: {
    ...BUTTON_STYLES.primary,
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },

  loginButtonEnabled: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    color: COLORS.white,
    boxShadow: `0 4px 16px ${COLORS.primary}40`,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 24px ${COLORS.primary}50`,
    },
  },

  loginButtonDisabled: {
    background: COLORS.grayLight,
    color: COLORS.text.secondary,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },

  // Loading spinner
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid transparent',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },

  // Error section - modern alert design
  errorSection: {
    marginTop: '24px',
  },

  errorContainer: {
    padding: '16px 20px',
    backgroundColor: '#fef2f2',
    border: `2px solid ${COLORS.primary}30`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },

  errorIcon: {
    color: COLORS.primary,
    marginTop: '2px',
    flexShrink: 0,
  },

  errorText: {
    ...TYPOGRAPHY.body,
    color: COLORS.primary,
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: 0,
  },

  offlineIndicator: {
    ...TYPOGRAPHY.caption,
    color: COLORS.text.secondary,
    fontStyle: 'italic',
    marginTop: '8px',
    textAlign: 'center',
  },

  // Unverified email section - modern warning card
  unverifiedSection: {
    marginTop: '24px',
    padding: '20px',
    background: `linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)`,
    border: `2px solid ${COLORS.secondary}30`,
    borderRadius: '16px',
    position: 'relative',
  },

  unverifiedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },

  unverifiedIcon: {
    color: COLORS.secondary,
    fontSize: '20px',
  },

  unverifiedTitle: {
    ...TYPOGRAPHY.heading4,
    color: COLORS.text.primary,
    fontWeight: '600',
    margin: 0,
  },

  unverifiedText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '14px',
    lineHeight: 1.5,
    marginBottom: '16px',
  },

  resendButton: {
    ...BUTTON_STYLES.secondary,
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: `2px solid ${COLORS.secondary}`,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: COLORS.secondary,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

  resendButtonEnabled: {
    '&:hover': {
      backgroundColor: COLORS.secondary,
      color: COLORS.white,
      transform: 'translateY(-1px)',
    },
  },

  resendButtonDisabled: {
    backgroundColor: COLORS.grayLight,
    color: COLORS.text.secondary,
    borderColor: COLORS.grayLight,
    cursor: 'not-allowed',
  },

  // Signup prompt - modern call-to-action
  signupSection: {
    marginTop: '32px',
    textAlign: 'center',
    padding: '24px',
    borderRadius: '16px',
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    border: `1px solid ${COLORS.primary}10`,
  },

  signupText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    marginBottom: '16px',
  },

  signupButton: {
    ...BUTTON_STYLES.outlined,
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '12px',
    backgroundColor: 'transparent',
    color: COLORS.primary,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
  },

  signupButtonHover: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${COLORS.primary}30`,
  },

  // Responsive design
  '@media (max-width: 768px)': {
    loginCard: {
      padding: '32px 24px',
      margin: '16px',
      borderRadius: '20px',
    },
    
    title: {
      fontSize: '28px',
    },
    
    brandText: {
      fontSize: '24px',
    },
  },
};